/**
 * PLAT-10918 — has the transcript changed since this control generated its text?
 *
 * Epic decision E5: staleness is **flagged, not fixed**. Nothing in here
 * regenerates, and nothing writes — the fingerprint is written by PLAT-10915
 * and refreshed by PLAT-10917. This only compares.
 *
 * Three rules the compare has to get right, in the order they are easy to get
 * wrong:
 *
 *  - **One definition of the hash (DS1).** Re-fingerprint through
 *    `fingerprintAITextBaseText`, the same function that wrote the stored value.
 *    A compare that normalized even slightly differently from the write would
 *    show "Transcript changed" on every graphic, forever, from the first render.
 *  - **The STORED scope, not the selector's (DS2).** Provenance records which
 *    scope the run read; the compare re-resolves that one. Reading the scope the
 *    creator is currently looking at would flag a perfectly fresh
 *    selection-generated hook the moment they flipped the toggle to Clip — an
 *    indicator about their UI state rather than about the transcript.
 *  - **No provenance is not stale (DS5).** A graphic that never generated, or
 *    whose generation failed, shows nothing. Absence of a fingerprint means "we
 *    do not know", and that must not read as "your text is wrong".
 *
 * DS3 falls out of DS2 rather than being special-cased: a `selection`-scoped
 * graphic re-resolves through `findOverlayRanges` and so only sees edits to its
 * OWN words, while a `clip`-scoped one legitimately flags on any edit in the
 * clip because it read all of it. That is what keeps the indicator from becoming
 * noise on a long clip. It also means a ripple edit that moves the passage
 * without changing the words re-resolves to the same text and correctly does not
 * flag (E4 — the anchor travels with the words).
 *
 * **Normalization, stated so it is a decision rather than an accident:**
 * `normalizeAITextBaseText` lowercases, strips punctuation and collapses
 * whitespace, so a casing, punctuation or spacing edit does NOT flag — only a
 * change to what was said does. Cutting a word the graphic read flags it, and so
 * does cutting every word it read (the base text resolves to nothing, whose
 * fingerprint is not the stored one — correct: the hook now quotes a line that
 * is gone).
 */
import { fingerprintAITextBaseText } from '@src/utility/aiTextFingerprint';
import { useMemo } from 'react';

import { resolveAITextBaseText } from './useAITextBaseText';

import type { AITextBaseTextScope } from '../types';
import type { AITextTranscriptElement } from './useAITextBaseText';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';

export interface UseAITextStalenessParams {
  /**
   * Hash of the base text the last generation read, from the provenance blob.
   * `undefined` when the control has never generated (DS5).
   */
  sourceFingerprint?: string;
  /**
   * The scope THAT run read (DS2) — deliberately not the scope the control's
   * selector currently shows. Missing or unrecognized alongside a fingerprint
   * written by an older blob shape, which is the same "we do not know" as no
   * fingerprint at all.
   */
  generatedBaseTextScope?: AITextBaseTextScope;
  /** The graphic's timeline marker — what `selection` resolves through. */
  overlay: TimelineMarker | null | undefined;
  /** The clip's transcript elements. */
  transcriptElements: ReadonlyArray<AITextTranscriptElement> | null | undefined;
}

export interface AITextStaleness {
  /** True when the current base text no longer hashes to the stored value. */
  isStale: boolean;
}

const NOT_STALE: AITextStaleness = { isStale: false };

export function useAITextStaleness({
  sourceFingerprint,
  generatedBaseTextScope,
  overlay,
  transcriptElements,
}: UseAITextStalenessParams): AITextStaleness {
  // DS6 — re-resolving a clip's base text means walking a transcript array that
  // is routinely thousands of elements, and this renders inside a popover with a
  // text field in it. Memoized on the inputs that can actually change the
  // answer, and short-circuited before the walk for the controls that have
  // nothing to compare against.
  return useMemo(
    () =>
      resolveAITextStaleness({
        sourceFingerprint,
        generatedBaseTextScope,
        overlay,
        transcriptElements,
      }),
    [sourceFingerprint, generatedBaseTextScope, overlay, transcriptElements],
  );
}

/** The hook's body, callable outside React (and directly in unit tests). */
export function resolveAITextStaleness({
  sourceFingerprint,
  generatedBaseTextScope,
  overlay,
  transcriptElements,
}: UseAITextStalenessParams): AITextStaleness {
  // The scope is TYPED as the union but arrives from persisted JSON, and
  // `getAiTextProvenance` only validates the fingerprint and the options array.
  // Checked rather than trusted because `resolveAITextBaseText` treats anything
  // that isn't `'clip'` as `'selection'`: an unrecognized value would silently
  // compare a clip-scoped graphic against its selection and flag it forever.
  // Unrecognized is "we do not know", same as absent (DS5).
  if (!sourceFingerprint || !isKnownScope(generatedBaseTextScope)) {
    return NOT_STALE;
  }

  const current = resolveAITextBaseText({
    scope: generatedBaseTextScope,
    overlay,
    transcriptElements,
  });

  return {
    isStale: fingerprintAITextBaseText(current.text) !== sourceFingerprint,
  };
}

function isKnownScope(
  scope: AITextBaseTextScope | undefined,
): scope is AITextBaseTextScope {
  return scope === 'clip' || scope === 'selection';
}
